import { AccessedFieldDefinition, canonicalValueType } from './types';

// Matches the legacy detail-page convention for empty values (e.g. "Personal Information" table).
export const EMPTY_VALUE_PLACEHOLDER = '-NA-';

export const isEmptyFieldValue = (raw: string | null | undefined): raw is null | undefined | '' =>
  raw === null || raw === undefined || raw === '';

export const getEditableFields = (
  definitions: AccessedFieldDefinition[],
): AccessedFieldDefinition[] => definitions.filter((d) => d.effectiveAccess === 'EDIT');

export const enumOptions = (def: AccessedFieldDefinition): Array<{ key: string; label: string }> => {
  const enumValues = def.enumValues as Record<string, { label?: string; deprecated?: boolean }> | string[] | null | undefined;
  if (!enumValues) return [];
  if (Array.isArray(enumValues)) return enumValues.map((v) => ({ key: v, label: v }));
  return Object.entries(enumValues)
    .filter(([, v]) => !v?.deprecated)
    .map(([key, v]) => ({ key, label: v?.label ?? key }));
};

export const formatFieldValue = (def: AccessedFieldDefinition, raw: string | null | undefined): string => {
  if (isEmptyFieldValue(raw)) return EMPTY_VALUE_PLACEHOLDER;
  switch (canonicalValueType(def.valueType)) {
    case 'BOOLEAN':
      return raw === 'true' || raw === '1' ? 'Yes' : 'No';
    case 'DATE': {
      const d = new Date(raw.includes('T') ? raw : `${raw}T00:00:00`);
      return Number.isNaN(d.getTime())
        ? raw
        : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    case 'CURRENCY': {
      const n = Number(raw);
      return Number.isNaN(n) ? raw : `₹${n.toLocaleString('en-IN')}`;
    }
    case 'ENUM': {
      const enumValues = def.enumValues as Record<string, { label?: string }> | string[] | null | undefined;
      if (enumValues && !Array.isArray(enumValues)) return enumValues[raw]?.label ?? raw;
      return raw;
    }
    default:
      return raw;
  }
};

/**
 * Format a value coming from a bulk-upload staging row (file_row.customFields).
 * Values there are normalized template values: MONEY is a JSON string like
 * '{"amount_paise":123400,"currency":"INR"}' — decode it to rupees before the
 * regular formatting. Returns undefined for blanks (empty preview cell).
 */
export const formatBulkPreviewFieldValue = (
  def: AccessedFieldDefinition,
  raw: string | null | undefined,
): string | undefined => {
  if (isEmptyFieldValue(raw)) return undefined;
  let value = raw;
  if (canonicalValueType(def.valueType) === 'CURRENCY' && raw.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(raw) as { amount_paise?: unknown };
      const paise = Number(parsed.amount_paise);
      if (!Number.isNaN(paise)) value = String(paise / 100);
    } catch {
      // leave raw as-is; formatFieldValue falls back to the raw string
    }
  }
  return formatFieldValue(def, value);
};
